import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface FormInputFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  type?: string;
  inputClassName?: string;
  className?: string;
  /** Icon/element rendered inside the input on the left side. */
  icon?: React.ReactNode;
  /** Separate pill rendered before the input (e.g. a `/e/` URL prefix). */
  prefix?: React.ReactNode;
  /** Rendered on the right of the label row (e.g. visibility eye). */
  labelAction?: React.ReactNode;
  min?: string | number;
  max?: string | number;
  disabled?: boolean;
  readOnly?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const FormInputField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  required,
  type = 'text',
  inputClassName,
  className,
  icon,
  prefix,
  labelAction,
  min,
  max,
  disabled,
  readOnly,
  onBlur,
}: FormInputFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <div className="flex items-center justify-between gap-2 min-h-5">
          <FormLabel className="mb-0">
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
          {labelAction ? <div className="shrink-0">{labelAction}</div> : null}
        </div>
        <FormControl>
          <Input
            type={type}
            placeholder={placeholder}
            className={inputClassName}
            icon={icon}
            prefix={prefix}
            min={min}
            max={max}
            disabled={disabled}
            readOnly={readOnly}
            {...field}
            onBlur={(event) => {
              onBlur?.(event);
              field.onBlur();
            }}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormInputField.displayName = 'FormInputField';
